import { useEffect, useState, useCallback, useRef } from "react";
import { motion, useMotionValue } from "framer-motion";
import { toast } from "sonner";
import { UserRound, X, Send, ChevronRight, Plus, Loader2, LifeBuoy } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { AttachPicker, AttList } from "./SupportAttachments";

const STATUS = {
  open: { label: "قيد المعالجة", cls: "bg-amber-100 text-amber-700" },
  answered: { label: "تم الرد", cls: "bg-emerald-100 text-emerald-700" },
  closed: { label: "مغلقة", cls: "bg-slate-100 text-slate-500" },
};
const fmt = (s) => String(s || "").slice(0, 16).replace("T", " ");

export const SupportWidget = () => {
  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [unread, setUnread] = useState(0);
  const [view, setView] = useState("list"); // list | new | thread
  const [active, setActive] = useState(null);
  const [subject, setSubject] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [atts, setAtts] = useState([]);
  const scrollRef = useRef(null);
  const POS_KEY = "support_widget_pos";
  const savedPos = (() => { try { return JSON.parse(localStorage.getItem(POS_KEY)) || { x: 0, y: 0 }; } catch { return { x: 0, y: 0 }; } })();
  const x = useMotionValue(savedPos.x);
  const y = useMotionValue(savedPos.y);
  const didDrag = useRef(false);
  const [constraints, setConstraints] = useState({ left: 0, right: 0, top: 0, bottom: 0 });

  useEffect(() => {
    const compute = () => setConstraints({
      left: 0,
      right: Math.max(0, window.innerWidth - 200),
      top: -Math.max(0, window.innerHeight - 140),
      bottom: 0,
    });
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const load = useCallback(async () => {
    try {
      const d = await api.supportTickets();
      setTickets(d.items || []);
      setUnread(d.unread || 0);
      setActive((a) => (a ? (d.items || []).find((t) => t.id === a.id) || a : a));
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (view === "thread" && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [view, active]);

  const openThread = async (tkt) => {
    setActive(tkt); setView("thread");
    if (tkt.org_unread) { try { await api.markTicketSeen(tkt.id); load(); } catch { /* ignore */ } }
  };

  const createTicket = async () => {
    if (!msg.trim() && !atts.length) { toast.warning("اكتب وصف المشكلة"); return; }
    setBusy(true);
    try {
      const tkt = await api.createSupportTicket(subject.trim(), msg.trim(), atts);
      toast.success("تم إرسال تذكرتك للدعم الفني");
      setSubject(""); setMsg(""); setAtts([]); await load(); setActive(tkt); setView("thread");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setBusy(false); }
  };

  const sendReply = async () => {
    if ((!msg.trim() && !atts.length) || !active) return;
    setBusy(true);
    try {
      const res = await api.addSupportMessage(active.id, msg.trim(), atts);
      setActive((a) => ({ ...a, messages: [...(a.messages || []), res.message] }));
      setMsg(""); setAtts([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setBusy(false); }
  };

  return (
    <>
      {/* Floating animated draggable button */}
      <motion.button
        data-testid="support-widget-button"
        onClick={() => { if (didDrag.current) { didDrag.current = false; return; } setOpen(true); setView("list"); }}
        drag
        dragMomentum={false}
        dragElastic={0}
        dragConstraints={constraints}
        onDragStart={() => { didDrag.current = true; }}
        onDragEnd={() => { localStorage.setItem(POS_KEY, JSON.stringify({ x: x.get(), y: y.get() })); }}
        style={{ x, y, touchAction: "none" }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ scale: { duration: 0.4 }, opacity: { duration: 0.4 } }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        whileDrag={{ scale: 1.08, cursor: "grabbing" }}
        className="fixed bottom-5 left-4 sm:bottom-6 sm:left-6 z-40 flex items-center gap-2 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 p-4 sm:pl-5 sm:pr-4 sm:py-3 text-white shadow-xl shadow-blue-600/30 cursor-grab active:cursor-grabbing"
        aria-label="الدعم الفني"
      >
        <span className="absolute inset-0 rounded-full bg-blue-500/40 animate-ping pointer-events-none" style={{ animationDuration: "2.5s" }} />
        <UserRound className="relative h-5 w-5" />
        <span className="relative font-bold text-sm whitespace-nowrap hidden sm:inline">الدعم الفني</span>
        {unread > 0 && (
          <span data-testid="support-unread-badge" className="absolute -top-1.5 -right-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-rose-500 px-1 text-[11px] font-bold text-white ring-2 ring-white">{unread}</span>
        )}
      </motion.button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md font-body p-0 overflow-hidden gap-0" dir="rtl" data-testid="support-dialog">
          <DialogHeader className="bg-gradient-to-l from-blue-600 to-indigo-700 text-white p-4 text-right space-y-0">
            <DialogTitle className="font-heading text-lg flex items-center gap-2 text-white">
              <LifeBuoy className="h-5 w-5" /> الدعم الفني
            </DialogTitle>
            <DialogDescription className="text-blue-100 text-xs">أرسل مشكلتك الفنية وسيرد عليك فريق دعم الموقع.</DialogDescription>
          </DialogHeader>

          <div className="p-4 max-h-[70vh] flex flex-col">
            {view === "list" && (
              <>
                <Button data-testid="support-new-btn" onClick={() => { setView("new"); setSubject(""); setMsg(""); }}
                  className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5 mb-3">
                  <Plus className="h-4 w-4" /> تذكرة دعم جديدة
                </Button>
                <div className="space-y-2 overflow-y-auto" data-testid="support-ticket-list">
                  {tickets.length === 0 ? (
                    <p className="text-center text-slate-400 text-sm py-8">لا توجد تذاكر بعد. أنشئ تذكرة لبدء المحادثة مع الدعم.</p>
                  ) : tickets.map((t) => {
                    const st = STATUS[t.status] || STATUS.open;
                    const last = (t.messages || [])[t.messages.length - 1];
                    return (
                      <button key={t.id} data-testid={`support-ticket-${t.id}`} onClick={() => openThread(t)}
                        className="w-full text-right rounded-xl border border-slate-200 p-3 hover:border-blue-300 hover:bg-slate-50 transition relative">
                        {t.org_unread && <span className="absolute top-3 left-3 h-2.5 w-2.5 rounded-full bg-rose-500" />}
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-slate-800 text-sm truncate">{t.subject}</span>
                          <span className={`text-[10px] rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                        </div>
                        {last && <p className="text-xs text-slate-500 mt-1 truncate">{last.sender === "support" ? "الدعم: " : ""}{last.text}</p>}
                        <p className="text-[10px] text-slate-300 font-mono-nums mt-1" dir="ltr">{fmt(t.updated_at)}</p>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {view === "new" && (
              <div className="space-y-3" data-testid="support-new-form">
                <button onClick={() => setView("list")} className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-700">
                  <ChevronRight className="h-3.5 w-3.5" /> رجوع
                </button>
                <div>
                  <label className="text-xs font-medium text-slate-600">عنوان المشكلة (اختياري)</label>
                  <Input data-testid="support-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)}
                    placeholder="مثال: مشكلة في رفع الرواتب" className="mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">وصف المشكلة الفنية</label>
                  <Textarea data-testid="support-message-input" rows={5} value={msg} onChange={(e) => setMsg(e.target.value)}
                    placeholder="اشرح المشكلة التي تواجهك في الموقع بالتفصيل…" className="mt-1" />
                </div>
                <AttachPicker value={atts} onChange={setAtts} testid="support-new-attach" />
                <Button data-testid="support-send-btn" onClick={createTicket} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال للدعم الفني
                </Button>
              </div>
            )}

            {view === "thread" && active && (
              <div className="flex flex-col h-[60vh]" data-testid="support-thread">
                <div className="flex items-center justify-between gap-2 pb-2 border-b border-slate-100">
                  <button onClick={() => { setView("list"); setMsg(""); setAtts([]); }} className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-700">
                    <ChevronRight className="h-3.5 w-3.5" /> رجوع
                  </button>
                  <span className="font-semibold text-slate-800 text-sm truncate">{active.subject}</span>
                </div>
                <div ref={scrollRef} className="flex-1 overflow-y-auto py-3 space-y-2">
                  {(active.messages || []).map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "support" ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "support" ? "bg-slate-100 text-slate-800 rounded-bl-sm" : "bg-blue-600 text-white rounded-br-sm"}`}>
                        <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                        <AttList items={m.attachments} light={m.sender !== "support"} />
                        <p className={`text-[9px] mt-1 font-mono-nums ${m.sender === "support" ? "text-slate-400" : "text-blue-100"}`} dir="ltr">{fmt(m.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-slate-100"><AttachPicker value={atts} onChange={setAtts} testid="support-reply-attach" /></div>
                <div className="flex items-end gap-2 pt-2">
                  <Textarea data-testid="support-reply-input" rows={1} value={msg} onChange={(e) => setMsg(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    placeholder="اكتب رسالتك…" className="flex-1 min-h-[40px] max-h-28 resize-none" />
                  <Button data-testid="support-reply-btn" onClick={sendReply} disabled={busy || (!msg.trim() && !atts.length)} size="icon" className="bg-blue-600 hover:bg-blue-700 shrink-0">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
